import {
  useMemo,
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
  type ComponentProps,
} from "react";
import {
  ResponsiveGridLayout,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
} from "react-grid-layout";
import { X } from "lucide-react";
import { WidgetCard } from "./WidgetCard";
import { widgetRegistry } from "@/widgets/registry";
import {
  DashboardFilterProvider,
  useDashboardFilter,
  FILTER_LABELS,
} from "@/contexts/DashboardFilterContext";
import type { DashboardMode } from "@/types/dashboard";
import type { WidgetPlacement } from "@/types/widget";
import { placementKey } from "@/types/widget";

const MIN_GRID_WIDTH = 960;

function useStableContainerWidth() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const rafRef = useRef<number | null>(null);
  const resizingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setWidth(Math.max(entry.contentRect.width, MIN_GRID_WIDTH));
      });
      // Suppress grid-item transitions for the duration of the container animation
      setIsResizing(true);
      if (resizingTimerRef.current) clearTimeout(resizingTimerRef.current);
      resizingTimerRef.current = setTimeout(() => setIsResizing(false), 350);
    });
    observer.observe(el);
    // Initial measurement
    setWidth(Math.max(el.getBoundingClientRect().width, MIN_GRID_WIDTH));
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (resizingTimerRef.current) clearTimeout(resizingTimerRef.current);
    };
  }, []);

  return { width, containerRef, isResizing };
}
const COLS = { xl: 12, lg: 12, md: 10, sm: 6, xs: 4 };
const ROW_HEIGHT = 60; // px per grid unit
const BREAKPOINTS = { xl: 1536, lg: 1280, md: 960, sm: 640, xs: 0 };

const MARGIN: [number, number] = [10, 10];

// Active filters
const TYPE_DISPLAY: Record<string, string> = {
  restreaming: "Restreaming",
  cdn_leeching: "Leeching",
  theft_for_restreaming: "Theft for Restreaming",
};

export function ActiveFilterBar() {
  const { filters, setFilter, clearFilters, hasActiveFilters } = useDashboardFilter();
  if (!hasActiveFilters) return null;

  const chips = (Object.entries(filters) as [string, any][])
    .filter(([, v]) => v !== undefined && !Array.isArray(v))
    .map(([key, value]) => ({
      key,
      label: `${FILTER_LABELS[key] ?? key}: ${TYPE_DISPLAY[value] ?? value}`,
    }));

  return (
    <div className="flex items-center gap-2 px-1 py-2 mb-2 flex-wrap">
      <span className="text-xs text-muted">Filtered by</span>
      {chips.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => setFilter({ [key]: (filters as any)[key] })}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30 hover:bg-brand-cyan/25 transition-colors"
        >
          {label}
          <X className="h-3 w-3 shrink-0" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          onClick={clearFilters}
          className="text-xs text-muted hover:text-primary transition-colors ml-1"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

export function GridLayoutCore({
  placements,
  mode,
  onLayoutChange,
  onRenameWidget,
  onDuplicateWidget,
  onRemoveWidget,
  moveSections,
  onMoveToSection,
}: {
  placements: WidgetPlacement[];
  mode: DashboardMode;
  onLayoutChange?: (placements: WidgetPlacement[]) => void;
  onRenameWidget?: (instanceId: string, title: string) => void;
  onDuplicateWidget?: (instanceId: string) => void;
  onRemoveWidget?: (instanceId: string) => void;
  moveSections?: { id: string; name: string }[];
  onMoveToSection?: (instanceId: string, sectionId: string) => void;
}) {
  const isEdit = mode === "edit";

  const { width, containerRef, isResizing } = useStableContainerWidth();

  // Build a single-breakpoint layout from placements
  const layouts: ResponsiveLayouts = useMemo(() => {
    const items: LayoutItem[] = placements
      .filter((p) => !p.hidden)
      .map((p) => ({
        ...p.layout,
        i: placementKey(p),
        static: !isEdit,
      }));
    return { xl: items, lg: items, md: items, sm: items, xs: items };
  }, [placements, isEdit]);

  const handleLayoutChange = useCallback(
    (currentLayout: Layout, allLayouts: ResponsiveLayouts) => {
      if (!onLayoutChange) return;
      // Use "lg" as canonical source of truth
      const canonical = (allLayouts.lg ?? currentLayout) as LayoutItem[];
      const updated: WidgetPlacement[] = placements.map((p) => {
        const item = canonical.find((l: LayoutItem) => l.i === placementKey(p));
        return item ? { ...p, layout: item } : p;
      });
      onLayoutChange(updated);
    },
    [placements, onLayoutChange],
  );

  const visiblePlacements = placements.filter((p) => !p.hidden);

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 ${isEdit ? "overflow-x-clip px-0.5 is-edit" : "overflow-x-auto"} ${isResizing ? "[&_.react-grid-item]:transition-none!" : ""}`}
    >
      {width === 0 ? null : (
        <ResponsiveGridLayout
          width={width}
          className="layout"
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          containerPadding={[0, 0]}
          dragConfig={{ handle: ".drag-handle" }}
          resizeConfig={{ handles: ["se", "sw", "ne", "nw", "e", "w", "s", "n"] }}
          onLayoutChange={handleLayoutChange}
        >
          {visiblePlacements.map((placement, index) => {
            const entry = widgetRegistry.get(placement.widgetId);
            if (!entry) return null;

            const pKey = placementKey(placement);
            const { component: WidgetComponent, meta } = entry;
            const title = placement.title ?? meta.title;

            return (
              <div key={pKey} style={{ overflow: "visible" }}>
                <WidgetCard
                  title={title}
                  category={meta.category}
                  index={index}
                  editMode={isEdit}
                  onRename={onRenameWidget ? (t) => onRenameWidget(pKey, t) : undefined}
                  onDuplicate={onDuplicateWidget ? () => onDuplicateWidget(pKey) : undefined}
                  onRemove={onRemoveWidget ? () => onRemoveWidget(pKey) : undefined}
                  moveSections={moveSections}
                  onMoveToSection={
                    onMoveToSection ? (sid) => onMoveToSection(pKey, sid) : undefined
                  }
                >
                  <WidgetComponent />
                </WidgetCard>
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}

export function GridLayout(props: ComponentProps<typeof GridLayoutCore>) {
  return (
    <DashboardFilterProvider>
      <GridLayoutCore {...props} />
    </DashboardFilterProvider>
  );
}
